import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RoomEvent, RoomStateEvent, ClientEvent, MatrixEventEvent, UserEvent, CryptoEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useAvatar } from '../context/AvatarContext';
import { useReceiptType } from '../context/SettingsContext';
import useRoomReadReceipts from './useRoomReadReceipts';
import useTimelineWindow from './useTimelineWindow';
import { useUnreadTracking } from './useUnreadTracking';
import { groupMessages } from '../utils/conversation';
import {
  SUPPORTED_MESSAGE_TYPES,
  TIMELINE_SYSTEM_EVENT_TYPES,
  CHRONOLOGICAL_PENDING_EVENTS_ERROR,
  PENDING_EVENT_OPERATION_TRANSLATION_KEY,
  PENDING_EVENT_OPERATION_ERROR_CODE,
} from '../constants';
import { useEventShields } from './useEventShields';
import { generateInitialsAvatar } from '../utils/avatar';
import { generateThumbnail, getMediaDimensions } from '../utils/thumbnailGenerator';
import logger from '../utils/logger';
import mediaCacheService from '../services/mediaCacheService';
import { getRoomDisplayName, getRoomTopic } from '../utils/gematikRoomUtils';
import { useDisambiguation } from '../context/DisambiguationContext';
import { getDMDisambiguatedName } from '../utils/disambiguationUtils';
import { sendReadMarkersWithCompliance } from '../utils/readMarkerUtils';
import { trustEventEmitter, TrustEvents } from '../crypto/events';
import { enrichEventWithMentions } from '../utils/mentionProcessing';
import { awaitRecipientTrustSettled, RECIPIENT_TRUST_SETTLE_MS } from '../utils/recipientTrust';
import { getMaxUploadSize } from '../utils/mediaConfig';
import { encryptAttachment } from 'matrix-encrypt-attachment';

// Timeout for Matrix SDK send operations (in milliseconds)
const SEND_TIMEOUT_MS = 120000; // 2 minutes

const ATTACHMENT_MESSAGE_TYPES = new Set(['m.file', 'm.image', 'm.video', 'm.audio']);
const FAILED_LOCAL_ECHO_STATUSES = new Set(['not_sent', 'cancelled']);
const RESENDABLE_EVENT_STATUSES = new Set(['not_sent', 'queued', 'encrypting']);
const SUCCESSFUL_PENDING_EVENT_STATUSES = new Set(['sent']);

const getEventMessageContent = (event) => event?.getClearContent?.() || event?.getContent?.() || {};

const isAttachmentEvent = (event) => ATTACHMENT_MESSAGE_TYPES.has(getEventMessageContent(event)?.msgtype);

const isFailedAttachmentLocalEcho = (event) => (
  isAttachmentEvent(event) && FAILED_LOCAL_ECHO_STATUSES.has(event?.status)
);

const isPendingLocalEchoEvent = (event) => event?.status != null && event.status !== 'sent';

const createPendingEventOperationError = () => {
  const error = new Error('The operation could not be completed. Please try again.');
  error.code = PENDING_EVENT_OPERATION_ERROR_CODE;
  error.translationKey = PENDING_EVENT_OPERATION_TRANSLATION_KEY;
  return error;
};

const normalizePendingEventOperationError = (error) => {
  const errorMessage = error?.message || error?.data?.error || '';
  if (errorMessage.includes(CHRONOLOGICAL_PENDING_EVENTS_ERROR)) {
    return createPendingEventOperationError();
  }

  return error;
};

const formatSendTimeoutMessage = (timeoutMs) => {
  if (timeoutMs % 60000 === 0) {
    const minutes = timeoutMs / 60000;
    return `Message send timed out after ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  const seconds = Math.ceil(timeoutMs / 1000);
  return `Message send timed out after ${seconds} seconds`;
};

// Timeout wrapper for Matrix SDK operations that can hang during E2E key sharing
const createTimeoutPromise = (timeoutMs, errorMessage) => {
  let timeoutId;
  const promise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(errorMessage);
      error.name = 'TimeoutError';
      error.timeoutMs = timeoutMs;
      reject(error);
    }, timeoutMs);
  });
  return { promise, clear: () => clearTimeout(timeoutId) };
};

const raceWithSendTimeout = (promise, timeoutMs) => {
  const { promise: timeoutPromise, clear: clearTimeoutFn } = createTimeoutPromise(
    timeoutMs,
    formatSendTimeoutMessage(timeoutMs),
  );

  return Promise.race([
    promise,
    timeoutPromise,
  ]).finally(clearTimeoutFn);
};

/**
 * A hook to manage a Matrix room's state, including its messages and timeline.
 * It provides a reactive list of messages and a function to send new messages.
 * Now includes timeline pagination and memory management via TimelineWindow.
 *
 * @param {string} roomId The ID of the room to connect to.
 * @returns {{
 *   roomId: string,
 *   name: string,
 *   members: import('matrix-js-sdk').RoomMember[],
 *   messageGroups: object[],
 *   sendMessage: (content: object) => Promise<{event_id: string}>,
 *   toggleReaction: (eventId: string, key: string, reactionEventId?: string, allReactions?: Map) => Promise<{event_id: string}>,
 *   deleteMessage: (eventId: string, reason?: string) => Promise<{event_id: string}>,
 *   reportMessage: (eventId: string, score?: number, reason?: string) => Promise<{success: boolean, error?: string}>,
 *   isRoomLoading: boolean,
 *   roomExists: boolean,
 *   isLoadingOlder: boolean,
 *   isLoadingNewer: boolean,
 *   canPaginateBackward: boolean,
 *   canPaginateForward: boolean,
 *   windowPosition: string,
 *   isFillingViewport: boolean,
 *   paginateBackward: () => Promise<void>,
 *   paginateForward: () => Promise<void>,
 *   jumpToLive: () => void,
 *   unreadCount: number,
 *   firstUnreadEventId: string | null,
 *   hasUnread: boolean,
 * }}
 * An object containing the client instance, the room's derived state, functions
 * to interact with it, loading/existence flags, and timeline pagination controls.
 */
export const useRoom = (roomId) => {
  const client = useMatrixClient();
  const receiptType = useReceiptType();
  const { needsDisambiguation } = useDisambiguation();
  const [room, setRoom] = useState(null);
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [members, setMembers] = useState([]);
  const [isRoomLoading, setIsRoomLoading] = useState(true);
  const [decryptionCounter, setDecryptionCounter] = useState(0);
  const [redactionCounter, setRedactionCounter] = useState(0);
  const [localEchoCounter, setLocalEchoCounter] = useState(0);
  const [memberAvatars, setMemberAvatars] = useState(new Map());
  const [readMarkerId, setReadMarkerId] = useState(null);
  const [roomAvatars, setRoomAvatars] = useState([]);
  const [encryptionStateCounter, setEncryptionStateCounter] = useState(0);
  const [powerLevelsCounter, setPowerLevelsCounter] = useState(0);
  const [ignoredUsers, setIgnoredUsers] = useState([]);

  // Track if we've successfully restored backup in this room session
  // to avoid redundant restores and help coordinate between verification events
  const backupRestoredRef = useRef(false);

  // Use the new timeline window hook for better pagination and memory management
  const {
    events,
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    refreshTimeline,
  } = useTimelineWindow(room, roomId);

  const readReceipts = useRoomReadReceipts(room);
  const eventShields = useEventShields(room);

  // Check if the room is encrypted by looking for m.room.encryption state event
  // Include encryptionStateCounter to recompute when state changes
  const isEncrypted = useMemo(() => {
    if (!room) return false;
    const encryptionEvent = room.currentState.getStateEvents('m.room.encryption', '');
    return !!encryptionEvent;
  }, [room, encryptionStateCounter]);

  // Check if the room is public by looking for m.room.join_rules state event
  const isPublic = useMemo(() => {
    if (!room) return false;
    const joinRulesEvent = room.currentState.getStateEvents('m.room.join_rules', '');
    return joinRulesEvent?.getContent()?.join_rule === 'public';
  }, [room, encryptionStateCounter]);

  // Effect 1: Find the room object and update the `room` state.
  useEffect(() => {
    logger.debug(`useRoom (finder): Effect triggered for roomId: ${roomId}`);
    if (!client || !roomId) {
      setRoom(null);
      setIsRoomLoading(false);
      return;
    }

    // Reset state when roomId changes
    setRoom(null);
    setName('');
    setMembers([]);
    setIsRoomLoading(true);
    backupRestoredRef.current = false; // Reset backup restoration tracking for new room

    const roomObject = client.getRoom(roomId);
    if (roomObject) {
      logger.debug(`useRoom (finder): Room ${roomId} found immediately.`);
      setRoom(roomObject);
      return;
    }

    logger.debug(`useRoom (finder): Room ${roomId} not found. Listening for ClientEvent.Room.`);
    const onClientRoom = (updatedRoom) => {
      if (updatedRoom.roomId === roomId) {
        logger.debug(`useRoom (finder): Matched room ${roomId} from ClientEvent.Room.`);
        setRoom(updatedRoom);
        client.removeListener(ClientEvent.Room, onClientRoom);
      }
    };
    client.on(ClientEvent.Room, onClientRoom);

    return () => {
      logger.debug(`useRoom (finder): Cleaning up ClientEvent.Room listener for ${roomId}.`);
      client.removeListener(ClientEvent.Room, onClientRoom);
    };
  }, [client, roomId]);

  // Effect 2: Set up listeners and initial state once we have a room object.
  useEffect(() => {
    if (!room || room.roomId !== roomId) {
      return; // Do nothing if we don't have a room object yet.
    }

    let cancelled = false;

    logger.debug(`useRoom (listener): Setting up for room ${room.roomId}`);

    // Compute and set the display name using the utility (prioritizes Gematik)
    let displayName = getRoomDisplayName(room);
    // Apply disambiguation for DM rooms where the other user has a duplicate name
    displayName = getDMDisambiguatedName(room.roomId, room, displayName, client.getUserId(), needsDisambiguation);
    setName(displayName);

    const roomTopic = getRoomTopic(room);
    setTopic(roomTopic);

    const allMembers = room.currentState.getMembers().filter(m =>
      m.membership === 'join' || m.membership === 'invite',
    );

    setMembers([...allMembers]);
    setIsRoomLoading(false);

    // Ensure full member list is loaded from server if needed (lazy loading support)
    // This calls GET /_matrix/client/v3/rooms/{roomId}/members when the local cache is incomplete
    room.loadMembersIfNeeded().then(() => {
      if (cancelled) return;
      const fullMembers = room.currentState.getMembers().filter(m =>
        m.membership === 'join' || m.membership === 'invite',
      );
      setMembers([...fullMembers]);
    }).catch(err => {
      if (cancelled) return;
      logger.warn(`[useRoom] Failed to load members from server for room ${room.roomId}:`, err.message);
    });

    // --- Set Initial Read Marker ---
    // This logic runs once when the room is loaded to find the initial "new messages" line.
    const myUserId = client.getUserId();
    if (myUserId) {
      // First, check for the 'm.fully_read' account data event. This is the most reliable marker.
      const fullyReadEvent = room.getAccountData('m.fully_read');
      const markerEventId = fullyReadEvent?.getContent()?.event_id;
      const readUpToId = room.getEventReadUpTo(myUserId);

      logger.debug('[useRoom] Setting initial readMarkerId:', {
        roomId: room.roomId,
        myUserId,
        fullyReadEventId: markerEventId,
        readUpToId,
        willUse: markerEventId || readUpToId,
      });

      logger.debug('[ReadMarker] markerEventId:', markerEventId);
      if (markerEventId) {
        setReadMarkerId(markerEventId);
      } else {
        // If no 'm.fully_read' marker, fall back to the user's latest read receipt.
        setReadMarkerId(readUpToId);
      }
    }

    // Define listeners for room state (not timeline - that's handled by useTimelineWindow)
    const onName = (roomInEvent) => {
      if (roomInEvent.roomId === room.roomId) {
        let displayName = getRoomDisplayName(roomInEvent);
        // Apply disambiguation for DM rooms
        displayName = getDMDisambiguatedName(room.roomId, roomInEvent, displayName, client.getUserId(), needsDisambiguation);
        setName(displayName);
      }
    };

    const onTopic = (roomInEvent) => {
      if (roomInEvent.roomId === room.roomId) setTopic(roomInEvent.topic);
    };

    const onStateUpdate = (state) => {
      if (state.roomId === room.roomId) {
        const allMembers = room.currentState.getMembers().filter(m =>
          m.membership === 'join' || m.membership === 'invite',
        );
        setMembers([...allMembers]);
        // Update name considering Gematik custom name
        let displayName = getRoomDisplayName(room);
        // Apply disambiguation for DM rooms
        displayName = getDMDisambiguatedName(room.roomId, room, displayName, client.getUserId(), needsDisambiguation);
        setName(displayName);

        const roomTopic = getRoomTopic(room);
        setTopic(roomTopic);
        // Trigger encryption state recalculation when state updates
        setEncryptionStateCounter(c => c + 1);
      }
    };

    // Listen for power level changes specifically
    const onRoomStateEvents = (event) => {
      if (event.getRoomId() === room.roomId && event.getType() === 'm.room.power_levels') {
        // Increment counter to trigger re-checks of delete permissions
        setPowerLevelsCounter(c => c + 1);
      }
    };

    // Listen for account data changes (e.g., m.fully_read marker updates)
    const onAccountData = (event) => {
      if (event.getType() === 'm.fully_read') {
        const newMarkerId = event.getContent()?.event_id;
        logger.debug('[useRoom] m.fully_read account data changed:', {
          roomId: room.roomId,
          newMarkerId,
        });
        if (newMarkerId) {
          setReadMarkerId(newMarkerId);
        }
      }
    };

    // Attach listeners (timeline listeners are handled by useTimelineWindow)
    room.on(RoomEvent.Name, onName);
    room.on(RoomEvent.Topic, onTopic);
    room.on(RoomEvent.AccountData, onAccountData);
    room.currentState.on(RoomStateEvent.Update, onStateUpdate);
    room.currentState.on(RoomStateEvent.Events, onRoomStateEvents);

    // Return a cleanup function
    return () => {
      cancelled = true;
      logger.debug(`useRoom (listener): Cleaning up listeners for room ${room.roomId}`);
      room.removeListener(RoomEvent.Name, onName);
      room.removeListener(RoomEvent.Topic, onTopic);
      room.removeListener(RoomEvent.AccountData, onAccountData);
      room.currentState.removeListener(RoomStateEvent.Update, onStateUpdate);
      room.currentState.removeListener(RoomStateEvent.Events, onRoomStateEvents);
    };
  }, [room, client, roomId, needsDisambiguation]);

  // Effect 3: Handle decryption events separately to avoid re-binding main listeners.
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const onEventDecrypted = (event) => {
      // If a decryption happens for an event in our room, we want to trigger
      // a re-calculation of messageGroups. A simple counter increment is the
      // cleanest way to do this without creating complex dependencies.
      if (event.getRoomId() === room.roomId) {
        logger.debug(`useRoom: Decryption event in ${room.roomId}, forcing update.`);
        setDecryptionCounter(c => c + 1);
      }
    };

    client.on(MatrixEventEvent.Decrypted, onEventDecrypted);
    return () => {
      client.removeListener(MatrixEventEvent.Decrypted, onEventDecrypted);
    };
  }, [client, room]); // This effect no longer depends on the messages array.

  // Effect 3b: Handle redactions (message deletions), mirroring the decryption
  // effect above. matrix-js-sdk applies a redaction by mutating its TARGET event
  // IN-PLACE (`makeRedacted`) — the event keeps the same object identity, so the
  // `events` array reference does not necessarily change and `messageGroups` would
  // not recompute on its own. This is especially true when the redaction arrives
  // over FEDERATION (a cross-homeserver room where the reader is on the remote HS)
  // and is applied without a fresh live RoomEvent.Timeline append, so the
  // liveEvent-gated timeline handler never fires. Bumping a counter forces
  // messageGroups to recompute so the bubble re-renders as "Message deleted"
  // instead of leaving the deleted plaintext on screen. (Cert:
  // TIM_V2_BASIS_AF_060104 — cross-org message deletion must hide the message
  // for the remote-HS recipient.)
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const onRedaction = (redactionEvent, eventRoom) => {
      // client.reEmitter forwards RoomEvent.Redaction for every room, so scope to ours.
      if (eventRoom?.roomId === room.roomId) {
        logger.debug(`useRoom: Redaction in ${room.roomId}, forcing update.`);
        setRedactionCounter(c => c + 1);
      }
    };

    client.on(RoomEvent.Redaction, onRedaction);
    return () => {
      client.removeListener(RoomEvent.Redaction, onRedaction);
    };
  }, [client, room]);

  // Effect 3a: Re-decrypt messages after key transfer via SAS verification
  // After successful device verification, keys may have been transferred but existing
  // encrypted messages won't automatically decrypt - we need to explicitly request
  // the SDK to re-decrypt them, which will emit MatrixEventEvent.Decrypted events.
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const handleVerificationComplete = async () => {
      logger.debug(`useRoom: VERIFICATION_COMPLETE received for room ${room.roomId}, triggering key restore and re-decryption`);

      try {
        // After SAS verification, restore keys from backup following Element Web's approach:
        // 1. Load the backup decryption key from Secret Storage (4S)
        // 2. Download megolm keys from backup
        // Reference: Element Web's SetupEncryptionStore.ts usePassPhrase() method
        const crypto = client.getCrypto();
        if (crypto) {
          // Step 1: Load the backup private key from secret storage
          // This makes the backup decryption key available to the crypto module
          if (typeof crypto.loadSessionBackupPrivateKeyFromSecretStorage === 'function') {
            logger.debug('useRoom: Loading backup key from secret storage...');
            try {
              await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
              logger.debug('useRoom: Backup key loaded from secret storage');
            } catch (loadErr) {
              // May fail if 4S isn't accessible or key was gossiped differently
              logger.debug('useRoom: Failed to load backup key from 4S (may use gossiped key):', loadErr.message);
            }
          }

          // Step 2: Restore the key backup (download all megolm keys)
          if (typeof crypto.restoreKeyBackup === 'function') {
            logger.debug('useRoom: Restoring keys from backup...');
            try {
              await crypto.restoreKeyBackup();
              logger.debug('useRoom: Key backup restore completed');
              backupRestoredRef.current = true; // Mark as restored to avoid redundant restores
            } catch (backupErr) {
              // Key backup restore may fail if not set up or keys not available
              // The KeyBackupDecryptionKeyCached event handler will retry when the key arrives
              logger.debug('useRoom: Key backup restore failed (will retry when key arrives via gossip):', backupErr.message);
            }
          }
        }

        // Wait a moment for keys to be processed by the crypto module
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Get the live timeline events
        const timeline = room.getLiveTimeline();
        const events = timeline.getEvents();

        // Find encrypted events that don't have clear content yet
        const encryptedEvents = events.filter(
          event => event.getType() === 'm.room.encrypted' && !event.getClearContent(),
        );

        if (encryptedEvents.length === 0) {
          logger.debug(`useRoom: No encrypted events to re-decrypt in room ${room.roomId}`);
          return;
        }

        logger.debug(`useRoom: Re-decrypting ${encryptedEvents.length} events in room ${room.roomId}`);

        // Request re-decryption for each encrypted event
        // The SDK will emit MatrixEventEvent.Decrypted for successfully decrypted events,
        // which will be caught by our onEventDecrypted handler above
        for (const event of encryptedEvents) {
          try {
            await client.decryptEventIfNeeded(event);
          } catch (err) {
            // Log but continue - some events may fail to decrypt
            logger.debug(`useRoom: Failed to decrypt event ${event.getId()}:`, err.message);
          }
        }

        logger.debug(`useRoom: Re-decryption pass complete for room ${room.roomId}`);
      } catch (err) {
        logger.error('useRoom: Error during re-decryption after verification:', err);
      }
    };

    trustEventEmitter.on(TrustEvents.VERIFICATION_COMPLETE, handleVerificationComplete);

    return () => {
      trustEventEmitter.off(TrustEvents.VERIFICATION_COMPLETE, handleVerificationComplete);
    };
  }, [client, room]);

  // Effect 3b: Handle local echo status updates to trigger re-renders when message sync status changes
  // This is needed so that the delete button visibility updates when a pending message syncs
  useEffect(() => {
    if (!room) {
      return;
    }

    const onLocalEchoUpdated = (event) => {
      // When a message's status changes (e.g., from SENDING to null/synced),
      // trigger a re-calculation of messageGroups so permission checks are re-run
      logger.debug(`useRoom: LocalEchoUpdated in ${room.roomId}, event: ${event.getId()}, status: ${event.status}`);
      setLocalEchoCounter(c => c + 1);
    };

    room.on(RoomEvent.LocalEchoUpdated, onLocalEchoUpdated);
    return () => {
      room.removeListener(RoomEvent.LocalEchoUpdated, onLocalEchoUpdated);
    };
  }, [room]);

  // Effect 3c: Attempt to decrypt encrypted events when room loads
  // This handles the case where keys were restored (e.g., via SAS verification in Settings)
  // while the user was away from the conversation. When they return, we need to try
  // decrypting any events that might now be decryptable with the newly available keys.
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const attemptDecryptionOnMount = async () => {
      try {
        const timeline = room.getLiveTimeline();
        const timelineEvents = timeline.getEvents();

        // Find encrypted events that don't have clear content yet
        const encryptedEvents = timelineEvents.filter(
          event => event.getType() === 'm.room.encrypted' && !event.getClearContent(),
        );

        if (encryptedEvents.length === 0) {
          return;
        }

        logger.debug(`useRoom: Room ${room.roomId} loaded with ${encryptedEvents.length} encrypted events, attempting decryption...`);

        // Try to decrypt each encrypted event
        // The SDK will emit MatrixEventEvent.Decrypted for successfully decrypted events
        for (const event of encryptedEvents) {
          try {
            await client.decryptEventIfNeeded(event);
          } catch {
            // Silent fail - event may not be decryptable yet (keys not available)
          }
        }
      } catch (err) {
        logger.debug('useRoom: Error attempting decryption on mount:', err.message);
      }
    };

    // Delay slightly to ensure room is fully loaded
    const timerId = setTimeout(attemptDecryptionOnMount, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [client, room]);

  // Effect 3d: Listen for backup decryption key being cached (from gossip/secret sharing)
  // This handles the race condition where the backup key arrives via secret gossiping
  // AFTER verification completes and our initial restoration attempts fail.
  // The SDK emits KeyBackupDecryptionKeyCached when a backup key is received from another
  // session via secret sharing or when loaded from secret storage.
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const handleBackupKeyCached = async (backupVersion) => {
      // Skip if we've already successfully restored backup for this room
      if (backupRestoredRef.current) {
        logger.debug(`useRoom: Backup already restored for room ${room.roomId}, skipping`);
        return;
      }

      logger.info(`useRoom: KeyBackupDecryptionKeyCached event received for version ${backupVersion} in room ${room.roomId}`);

      try {
        const crypto = client.getCrypto();
        if (!crypto) {
          logger.debug('useRoom: No crypto module available');
          return;
        }

        // Now that the backup key is cached, we can restore the key backup
        if (typeof crypto.restoreKeyBackup === 'function') {
          logger.debug('useRoom: Restoring keys from backup after key was cached...');
          try {
            await crypto.restoreKeyBackup();
            logger.info('useRoom: Key backup restored successfully after gossip');
            backupRestoredRef.current = true;
          } catch (backupErr) {
            logger.debug('useRoom: Key backup restore failed after gossip:', backupErr.message);
          }
        }

        // Wait a moment for keys to be processed
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Re-decrypt any encrypted events that might now be decryptable
        const timeline = room.getLiveTimeline();
        const timelineEvents = timeline.getEvents();
        const encryptedEvents = timelineEvents.filter(
          event => event.getType() === 'm.room.encrypted' && !event.getClearContent(),
        );

        if (encryptedEvents.length === 0) {
          logger.debug(`useRoom: No encrypted events to re-decrypt after key gossip in room ${room.roomId}`);
          return;
        }

        logger.debug(`useRoom: Re-decrypting ${encryptedEvents.length} events after key gossip in room ${room.roomId}`);

        for (const event of encryptedEvents) {
          try {
            await client.decryptEventIfNeeded(event);
          } catch (err) {
            logger.debug(`useRoom: Failed to decrypt event ${event.getId()} after gossip:`, err.message);
          }
        }

        logger.debug(`useRoom: Re-decryption pass complete after key gossip for room ${room.roomId}`);
      } catch (err) {
        logger.error('useRoom: Error during re-decryption after key gossip:', err);
      }
    };

    // Listen on the client for crypto events (the crypto module emits these via the client)
    client.on(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);

    return () => {
      client.off(CryptoEvent.KeyBackupDecryptionKeyCached, handleBackupKeyCached);
    };
  }, [client, room]);

  // Effect 4: Keep the crypto module's tracked user list in sync with room members.
  useEffect(() => {
    if (client && members.length > 0) {
      const userIds = members.map(m => m.userId);
      logger.debug(`useRoom: Tracking ${userIds.length} users for device list updates.`);
      // This tells the crypto module to be aware of these users' devices.
      // It's essential for calculating e2ee shield states correctly.
      // We don't need the result, but calling this triggers the download of device
      // keys for any users that are not already known.
      const crypto = client.getCrypto();
      if (crypto) {
        crypto.getUserDeviceInfo(userIds);
      }
    }
  }, [client, members]);

  // Create stable member identifier for avatar effect dependency
  // Only changes when members are added/removed, not on every state update
  const memberIds = useMemo(() => {
    return members.map(m => m.userId).sort().join(',');
  }, [members]);

  // Effect 5: Load member avatars asynchronously with authenticated fetch
  const { getUserAvatar } = useAvatar();

  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    let isMounted = true;

    const loadAvatars = async () => {
      const newAvatarMap = new Map();

      // Load all avatars in parallel
      await Promise.all(
        members.map(async (m) => {
          const displayName = m.rawDisplayName || m.name || m.userId;
          try {
            // Get avatar via context (handles lifecycle automatically)
            const avatarUrl = await getUserAvatar(m.userId, displayName, 96);

            if (isMounted) {
              newAvatarMap.set(m.userId, avatarUrl);
            }
          } catch (error) {
            logger.error(`Failed to load avatar for ${m.userId}:`, error);
            // Fallback to initials avatar
            if (isMounted) {
              newAvatarMap.set(m.userId, generateInitialsAvatar(displayName));
            }
          }
        }),
      );

      if (isMounted) {
        setMemberAvatars(newAvatarMap);
      }
    };

    loadAvatars();

    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, memberIds, getUserAvatar]);
  // Note: 'members' is intentionally omitted. We only want to re-run when member IDs change
  // (additions/removals), not when member objects are updated. The memberIds memo tracks this.

  // Listen for custom avatar change events (immediate updates on own upload)
  // This complements UserEvent.AvatarUrl which only fires for other users
  const { invalidateAvatar } = useAvatar();

  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    const handleCustomAvatarChange = async (event) => {
      const { userId: changedUserId } = event.detail || {};
      if (!changedUserId) {
        return;
      }

      // Check if this user is a member of this room
      const isMember = members.some(m => m.userId === changedUserId);
      if (!isMember) {
        return;
      }

      logger.debug(`Custom avatar event: ${changedUserId} in room ${roomId}`);

      // Invalidate cache via context
      await invalidateAvatar(changedUserId);

      // Re-fetch avatar for this specific user
      const member = members.find(m => m.userId === changedUserId);
      if (!member) return;

      const displayName = member.rawDisplayName || member.name || member.userId;

      try {
        const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

        // Update memberAvatars map
        setMemberAvatars(prev => {
          const updated = new Map(prev);
          updated.set(changedUserId, newAvatarUrl);
          return updated;
        });

        logger.debug(`Avatar updated: ${changedUserId} in room ${roomId}`);
      } catch (error) {
        logger.error(`Failed to update avatar for ${changedUserId}:`, error);
      }
    };

    window.addEventListener('matrix:avatar:changed', handleCustomAvatarChange);

    return () => {
      window.removeEventListener('matrix:avatar:changed', handleCustomAvatarChange);
    };
  }, [client, members, roomId, invalidateAvatar, getUserAvatar]);

  // Listen for Matrix SDK avatar change events (sync-based updates from other users)
  // IMPORTANT: We listen to BOTH UserEvent.AvatarUrl AND RoomStateEvent.Members for defense in depth.
  // UserEvent.AvatarUrl often doesn't fire when users change avatars, but RoomStateEvent.Members
  // always fires because avatar changes come as m.room.member state events. Keeping both listeners
  // ensures we catch avatar updates through either path.
  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    const handleUserAvatarChange = async (event, user) => {
      const changedUserId = user.userId;

      // Check if this user is a member of this room
      const isMember = members.some(m => m.userId === changedUserId);
      if (!isMember) {
        return;
      }

      logger.debug(`UserEvent.AvatarUrl: ${changedUserId} in room ${roomId}`);

      // Invalidate and re-fetch via context
      await invalidateAvatar(changedUserId);

      const member = members.find(m => m.userId === changedUserId);
      if (!member) return;

      const displayName = member.rawDisplayName || member.name || member.userId;

      try {
        const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

        setMemberAvatars(prev => {
          const updated = new Map(prev);
          updated.set(changedUserId, newAvatarUrl);
          return updated;
        });

        logger.debug(`Avatar updated: ${changedUserId} in room ${roomId}`);
      } catch (error) {
        logger.error(`Failed to update avatar for ${changedUserId}:`, error);
      }
    };

    // Listen for RoomStateEvent.Members to catch avatar changes in m.room.member events
    // This is the PRIMARY mechanism - UserEvent.AvatarUrl often doesn't fire for avatar changes
    const handleMemberStateChange = async (event) => {
      // Only process m.room.member events
      if (event.getType() !== 'm.room.member') return;

      // Only process events for this room
      if (event.getRoomId() !== roomId) return;

      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check if avatar_url changed
      if (content.avatar_url !== prevContent?.avatar_url) {
        const changedUserId = event.getStateKey();

        // Check if this user is a member of this room
        const isMember = members.some(m => m.userId === changedUserId);
        if (!isMember) {
          return;
        }

        logger.debug(`RoomStateEvent.Members avatar change: ${changedUserId} in room ${roomId}`);

        // Invalidate and re-fetch via context
        await invalidateAvatar(changedUserId);

        const memberObj = members.find(m => m.userId === changedUserId);
        if (!memberObj) return;

        const displayName = memberObj.rawDisplayName || memberObj.name || memberObj.userId;

        try {
          const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

          setMemberAvatars(prev => {
            const updated = new Map(prev);
            updated.set(changedUserId, newAvatarUrl);
            return updated;
          });

          logger.debug(`Avatar updated via RoomStateEvent: ${changedUserId} in room ${roomId}`);
        } catch (error) {
          logger.error(`Failed to update avatar for ${changedUserId}:`, error);
        }
      }
    };

    client.on(UserEvent.AvatarUrl, handleUserAvatarChange);
    client.on(RoomStateEvent.Members, handleMemberStateChange);

    return () => {
      client.off(UserEvent.AvatarUrl, handleUserAvatarChange);
      client.off(RoomStateEvent.Members, handleMemberStateChange);
    };
  }, [client, members, roomId, invalidateAvatar, getUserAvatar]);

  // Listen for ignored user list changes
  useEffect(() => {
    if (!client) return;

    // Track previous ignore list to detect removals (users being unignored)
    let prevIgnored = [];

    const loadIgnoredUsers = () => {
      try {
        const ignored = client.getIgnoredUsers() || [];

        // Detect if users were REMOVED from ignore list (unignored)
        const unignoredUsers = prevIgnored.filter(
          userId => !ignored.includes(userId),
        );

        // If users were unignored and we have a room, refresh timeline to get missed messages
        if (unignoredUsers.length > 0 && roomId) {
          logger.info('[useRoom] Users unignored, refreshing timeline to fetch missed messages:', unignoredUsers);
          refreshTimeline().catch(err => {
            logger.error('[useRoom] Failed to refresh timeline after unignore:', err);
          });
        }

        prevIgnored = ignored;
        setIgnoredUsers(ignored);
        logger.debug('Ignored users loaded:', ignored);
      } catch (error) {
        logger.error('Failed to load ignored users:', error);
      }
    };

    const handleAccountData = (event) => {
      if (event.getType() === 'm.ignored_user_list') {
        loadIgnoredUsers();
      }
    };

    // Load initial ignored users
    loadIgnoredUsers();

    // Listen for changes to ignored user list
    client.on(ClientEvent.AccountData, handleAccountData);

    return () => {
      client.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [client, roomId, refreshTimeline]);

  const retryPendingEventIfNeeded = useCallback(async (txnId, effectiveTimeoutMs, context) => {
    if (!client || !roomId || !txnId) {
      return { handled: false };
    }

    const roomObject = client.getRoom(roomId);
    const pendingEvent = roomObject?.getEventForTxnId?.(txnId);

    if (!roomObject || !pendingEvent) {
      return { handled: false };
    }

    const status = pendingEvent.status;
    logger.info('[useRoom] Found existing pending event for txnId', {
      roomId,
      txnId,
      context,
      eventId: pendingEvent.getId?.(),
      status,
    });

    if (SUCCESSFUL_PENDING_EVENT_STATUSES.has(status)) {
      logger.info('[useRoom] Pending event already sent; treating retry as success', {
        roomId,
        txnId,
        context,
        eventId: pendingEvent.getId?.(),
        status,
      });
      return {
        handled: true,
        result: { event_id: pendingEvent.getId?.() },
      };
    }

    if (status === 'sending') {
      const error = new Error('Message send is still in progress');
      error.name = 'SendInProgressError';
      throw error;
    }

    if (!RESENDABLE_EVENT_STATUSES.has(status)) {
      logger.warn('[useRoom] Pending event exists but is not resendable', {
        roomId,
        txnId,
        context,
        eventId: pendingEvent.getId?.(),
        status,
      });
      return { handled: false };
    }

    logger.info('[useRoom] Resending existing pending event', {
      roomId,
      txnId,
      context,
      eventId: pendingEvent.getId?.(),
      status,
      effectiveTimeoutMs,
    });

    const result = await raceWithSendTimeout(
      client.resendEvent(pendingEvent, roomObject),
      effectiveTimeoutMs,
    );

    return {
      handled: true,
      result,
    };
  }, [client, roomId]);

  const cancelPendingMessage = useCallback((txnId) => {
    if (!client || !roomId || !txnId || typeof client.cancelPendingEvent !== 'function') {
      return false;
    }

    const roomObject = client.getRoom(roomId);
    const pendingEvent = roomObject?.getEventForTxnId?.(txnId);

    if (!roomObject || !pendingEvent) {
      return false;
    }

    try {
      client.cancelPendingEvent(pendingEvent); //call fn from sdk to cancel the pending messages
      logger.info('[useRoom] Cancelled pending event', {
        roomId,
        txnId,
        eventId: pendingEvent.getId?.(),
        status: pendingEvent.status,
      });
      return true;
    } catch (error) {
      logger.warn('[useRoom] Failed to cancel pending event', {
        roomId,
        txnId,
        eventId: pendingEvent.getId?.(),
        status: pendingEvent.status,
        errorName: error?.name,
        errorMessage: error?.message,
      });
      return false;
    }
  }, [client, roomId]);

  const sendMessage = useCallback(async (content, options = {}) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    const effectiveTimeoutMs = Math.max(1, Math.min(options.timeoutMs ?? SEND_TIMEOUT_MS, SEND_TIMEOUT_MS));

    const retryResult = await retryPendingEventIfNeeded(options.txnId, effectiveTimeoutMs, 'message');
    if (retryResult.handled) {
      return retryResult.result;
    }

    let enrichedContent = content;

    try {
      // Only attempt enrichment if we have room access
      if (room) {
        // Get all room members from currentState (includes 'join' and 'invite' states)
        // Note: getJoinedMembers() only returns 'join' state which misses invited members
        const allMembers = room.currentState?.getMembers?.() || [];

        // Include both 'join' and 'invite' members for mention resolution
        // (users can mention invited members who haven't joined yet)
        const roomMembers = allMembers
          .filter(m => m.membership === 'join' || m.membership === 'invite')
          .map(m => ({
            userId: m.userId,
            name: m.name || m.rawDisplayName || m.userId,
            rawDisplayName: m.rawDisplayName,
          }));

        // Only enrich if we have members to resolve against
        if (roomMembers.length > 0) {
          // Get current user ID to exclude self-mentions
          const currentUserId = client.getUserId?.();

          // Enrich regular text and emote messages
          if ((content.msgtype === 'm.text' || content.msgtype === 'm.emote') && content.body) {
            enrichedContent = enrichEventWithMentions(content, roomMembers, currentUserId);
          }

          // Enrich edit messages (m.new_content field) for text and emotes
          const newContentMsgtype = content['m.new_content']?.msgtype;
          if ((newContentMsgtype === 'm.text' || newContentMsgtype === 'm.emote') && content['m.new_content']?.body) {
            enrichedContent = { ...content };
            enrichedContent['m.new_content'] = enrichEventWithMentions(content['m.new_content'], roomMembers, currentUserId);
          }
        }
      }
    } catch (error) {
      // Log but don't fail the send - mentions are enhancement, not critical
      logger.warn('[useRoom] Failed to enrich message with mentions:', error.message);
      enrichedContent = content; // Fallback to original
    }

    // A_25514: before the FIRST encrypted send to this room, wait (bounded,
    // best-effort) for recipient device trust to settle so the first megolm
    // key-share includes a freshly-enrolled-but-already-cross-signed recipient
    // device. Observe-only (never mutates trust), skips non-encrypted rooms, and
    // is a no-op on re-sends to an already-gated room. See utils/recipientTrust.
    if (room?.hasEncryptionStateEvent?.()) {
      const recipientIds = (room.currentState?.getMembers?.() || [])
        .filter(m => m.membership === 'join' || m.membership === 'invite')
        .map(m => m.userId);
      await awaitRecipientTrustSettled(client, roomId, recipientIds, RECIPIENT_TRUST_SETTLE_MS);
    }

    // Use 2-minute timeout to prevent indefinite hangs during E2E key sharing
    logger.info('[useRoom] Sending message', {
      roomId,
      txnId: options.txnId,
      retryCount: options.retryCount,
      effectiveTimeoutMs,
      requestedTimeoutMs: options.timeoutMs,
      msgtype: enrichedContent?.msgtype,
    });

    const { promise: timeoutPromise, clear: clearTimeoutFn } = createTimeoutPromise(
      effectiveTimeoutMs,
      formatSendTimeoutMessage(effectiveTimeoutMs),
    );
    const sendPromise = options.txnId
      ? client.sendMessage(roomId, enrichedContent, options.txnId)
      : client.sendMessage(roomId, enrichedContent);

    return Promise.race([
      sendPromise,
      timeoutPromise,
    ]).finally(clearTimeoutFn);
  }, [client, roomId, room, retryPendingEventIfNeeded]);

  const deleteMessage = useCallback(async (eventId, reason) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available for delete.');
    }

    // Find the event to check if it's an attachment that needs cache clearing.
    const event = room?.findEventById(eventId);
    const content = event ? getEventMessageContent(event) : null;
    const attachmentUrl = content?.url || content?.file?.url;

    const clearAttachmentCache = async () => {
      if (!isAttachmentEvent(event) || !attachmentUrl) {
        return;
      }

      try {
        logger.debug(`[useRoom] Deleting attachment from cache: ${attachmentUrl}`);
        await mediaCacheService.removeMedia(attachmentUrl);
      } catch (error) {
        // Log the error but don't block the message deletion.
        logger.error(`[useRoom] Failed to remove attachment from cache: ${attachmentUrl}`, error);
      }
    };

    if (event && isFailedAttachmentLocalEcho(event)) {
      if (typeof client.cancelPendingEvent !== 'function') {
        throw new Error('Matrix client cannot cancel pending attachments.');
      }

      try {
        client.cancelPendingEvent(event);
        logger.info('[useRoom] Deleted failed attachment local echo', {
          roomId,
          eventId,
          status: event.status,
        });
      } catch (error) {
        logger.error('[useRoom] Failed to delete failed attachment local echo', {
          roomId,
          eventId,
          status: event.status,
          errorName: error?.name,
          errorMessage: error?.message,
        });
        throw error;
      }

      await clearAttachmentCache();
      return { event_id: eventId, cancelled: true };
    }

    if (event) {
      if (isPendingLocalEchoEvent(event)) {
        logger.info('[useRoom] Blocking delete for pending local echo', {
          roomId,
          eventId,
          status: event.status,
        });
        throw createPendingEventOperationError();
      }

      await clearAttachmentCache();
    }

    // The `reason` can be passed in the content of the redaction event.
    const redactionContent = (reason) ? { reason } : {};
    try {
      return await client.redactEvent(roomId, eventId, undefined, redactionContent);
    } catch (error) {
      throw normalizePendingEventOperationError(error);
    }
  }, [client, roomId, room]);

  const leaveRoom = useCallback(async () => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    // Clear all cached media for this room from IndexedDB before leaving.
    try {
      logger.debug(`[useRoom] Clearing media cache for room on leave: ${roomId}`);
      await mediaCacheService.removeMediaForRoom(roomId);
    } catch (error) {
      logger.error(`[useRoom] Failed to clear media cache for room ${roomId}:`, error);
    }

    return client.leave(roomId);
  }, [client, roomId]);

  const toggleReaction = useCallback(async (eventId, key, reactionEventId = null, allReactions = new Map()) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    const targetEvent = room?.findEventById?.(eventId) || client.getRoom(roomId)?.findEventById?.(eventId);
    if (isPendingLocalEchoEvent(targetEvent)) {
      logger.info('[useRoom] Blocking reaction for pending local echo', {
        roomId,
        eventId,
        status: targetEvent.status,
      });
      throw createPendingEventOperationError();
    }

    const currentUserId = client.getUserId();

    // If reactionEventId is provided, this is an unreact action
    if (reactionEventId) {
      try {
        return await client.redactEvent(roomId, reactionEventId);
      } catch (error) {
        const normalizedError = normalizePendingEventOperationError(error);
        if (normalizedError !== error) {
          throw normalizedError;
        }

        // Only ignore "already gone" errors (optimistic UI / already removed on server).
        // Surface all other errors so the caller can show feedback and revert optimistic UI.
        const errcode = error?.errcode || error?.data?.errcode;
        if (errcode === 'M_NOT_FOUND' || error?.httpStatus === 404) {
          logger.debug('[toggleReaction] Reaction already removed');
          return;
        }
        throw error;
      }
    }

    // Check if user has already reacted with a different emoji
    let existingReactionEventId = null;
    allReactions.forEach((reactors, emoji) => {
      if (emoji !== key) { // Different emoji
        const userReaction = reactors.find(reactor => reactor.senderId === currentUserId);
        if (userReaction) {
          existingReactionEventId = userReaction.eventId;
        }
      }
    });

    // If user has an existing reaction with a different emoji, remove it first
    if (existingReactionEventId) {
      try {
        await client.redactEvent(roomId, existingReactionEventId);
      } catch (error) {
        const normalizedError = normalizePendingEventOperationError(error);
        if (normalizedError !== error) {
          throw normalizedError;
        }

        // Ignore errors for non-existent events
        logger.warn('[toggleReaction] Failed to redact existing reaction, continuing:', error.message);
      }
    }

    // TI-M requirement A_26227-01: the reaction key MUST be a single user-perceived
    // emoji glyph (one grapheme cluster). This permits base + variation selector (❤️),
    // skin-tone modifiers (👍🏽), ZWJ sequences (👩‍💻), and regional-indicator pairs (🇩🇪)
    // — i.e. anything Intl.Segmenter counts as one extended-grapheme cluster.
    // Pre-Baseline-2024 browsers (e.g. Firefox ESR 115) lack Intl.Segmenter — in that
    // case we skip strict client-side validation and let the server's A_26227-01 gate
    // catch malformed keys via the catch block below.
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      const segments = [...new Intl.Segmenter().segment(key)];
      if (segments.length !== 1) {
        logger.warn('[useRoom] Reaction key rejected: not a single grapheme cluster:', key);
        const err = new Error('This emoji is not supported as a reaction. Please use a single emoji.');
        err.translationKey = 'OperationErrors.emojiNotSingleCodePoint';
        throw err;
      }
    }

    // Send the new reaction
    const content = {
      'm.relates_to': {
        'rel_type': 'm.annotation',
        'event_id': eventId,
        'key': key,
      },
    };

    try {
      return await client.sendEvent(roomId, 'm.reaction', content);
    } catch (error) {
      // Check for the specific error message about emoji length/validity
      // Error comes in as A_26228, but the actual Matrix spec is A_26227-01
      const errorMessage = error.message || error.data?.error || '';
      if (errorMessage.includes('emoji') || errorMessage.includes('A_26228') || errorMessage.includes('A_26227')) {
        logger.warn('[useRoom] Emoji reaction rejected by server (likely too new):', key);
        // All emojis 13.0 and later are invalid
        const friendlyError = new Error('This emoji is not supported by the Matrix server. Try using a more common emoji like 👍 or 😊');
        friendlyError.translationKey = 'OperationErrors.emojiNotSupportedByServer';
        throw friendlyError;
      }
      throw normalizePendingEventOperationError(error);
    }
  }, [client, roomId]);

  /**
   * Report a message to server administrators
   * @param {string} eventId - The event ID to report
   * @param {number} score - Score from -100 (most offensive) to 0 (inoffensive), default -50
   * @param {string} reason - Reason for reporting the message
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const reportMessage = useCallback(async (eventId, score = -50, reason = '') => {
    if (!client || !roomId) {
      return { success: false, error: 'Client or room not available' };
    }

    if (!eventId) {
      return { success: false, error: 'Event ID is required' };
    }

    try {
      await client.reportEvent(roomId, eventId, score, reason);
      logger.info(`[useRoom] Message ${eventId} reported successfully`);
      return { success: true };
    } catch (error) {
      logger.error('[useRoom] Failed to report message:', error);
      return {
        success: false,
        error: error?.message || 'Failed to report message',
      };
    }
  }, [client, roomId]);

  const canDeleteMessage = useCallback(async (eventId) => {
    if (!client || !roomId || !room) {
      logger.warn('useRoom: Cannot check delete permissions - client, room, or roomId not available');
      return false;
    }

    try {
      // Get the event (message) to check
      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('useRoom: Event not found for delete permission check:', eventId);
        return false;
      }

      const currentUserId = client.getUserId();

      // Check if user is the sender of the message
      const isOwnMessage = event.getSender() === currentUserId;

      // Check if message is still pending (not synced to server)
      // status is null when message is fully synced, non-null when pending/sending
      const isPending = event.status !== null && event.status !== undefined;

      // Check if user has permission to redact events in this room
      let canRedact = false;

      if (room.currentState) {
        const powerLevels = room.currentState.getStateEvents('m.room.power_levels', '');

        if (powerLevels) {
          const powerLevelContent = powerLevels.getContent();
          const users_default = powerLevelContent.users_default ?? 0;
          const userPowerLevel = powerLevelContent.users?.[currentUserId] ?? users_default;
          const redactPowerLevel = powerLevelContent.redact ?? 50;

          // Per Matrix spec: Users can redact others' messages if power level >= redact level
          // Default redact level is 50 (moderator). Users can always delete their own messages.
          canRedact = userPowerLevel >= redactPowerLevel;
        }
      }

      // Non-admin users can only delete their own messages after they are synced
      // This prevents the "Cannot call getPendingEvents with pendingEventOrdering == chronological" error
      if (isOwnMessage && !canRedact && isPending) {
        logger.debug('useRoom: Delete blocked - own message still pending for non-admin user:', {
          roomId,
          eventId,
          status: event.status,
        });
        return false;
      }

      // User can delete if it's their own message OR they have redact permissions
      const canDelete = isOwnMessage || canRedact;

      logger.debug('useRoom: Delete permission check:', {
        roomId,
        eventId,
        currentUserId,
        isOwnMessage,
        canRedact,
        isPending,
        canDelete,
      });

      return canDelete;
    } catch (error) {
      logger.error('useRoom: Error checking delete permissions:', error);
      return false;
    }
  }, [client, roomId, room]);

  const setReadMarker = useCallback(async (eventId, destinationRoomId) => {
    if (!client || !destinationRoomId || !eventId) return;
    try {
      // Get the room and event objects for proper API call
      const targetRoom = client.getRoom(destinationRoomId);
      const event = targetRoom?.findEventById(eventId);

      if (!event) {
        logger.warn(`[ReadMarker] Event not found for read marker: ${eventId} in room ${destinationRoomId}`);
        // Still update local state even if event not found (may have been paginated out)
        if (destinationRoomId === roomId) {
          setReadMarkerId(eventId);
        }
        return;
      }

      await sendReadMarkersWithCompliance(
        client,
        destinationRoomId,
        eventId,
        event,
        receiptType,
      );

      // only update local state if it's not due to room change cleanup
      if (destinationRoomId === roomId) {
        setReadMarkerId(eventId);
      }
      logger.debug(`[ReadMarker] Read marker set to ${eventId} in room ${destinationRoomId}, current is ${roomId}`);
    } catch (error) {
      // Don't show a UI error for this, as it's a background task, but log it.
      logger.error(`[ReadMarker] Failed to set read marker for room ${destinationRoomId}:`, error);
    }
  }, [client, roomId, receiptType]);


  const sendAttachment = useCallback(async (file, caption = '', options = {}) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    if (!file) {
      throw new Error('File is required for attachment.');
    }

    const effectiveTimeoutMs = Math.max(1, Math.min(options.timeoutMs ?? SEND_TIMEOUT_MS, SEND_TIMEOUT_MS));

    const { abortController, thumbnailResult, onProgressUpdate, replyEventId } = options;

    // Validate file size against server's m.upload.size limit (CLIENT A_25518)
    const maxUploadSize = await getMaxUploadSize(client);
    if (file.size > maxUploadSize) {
      const error = new Error('FILE_TOO_LARGE');
      error.maxUploadSize = maxUploadSize;
      error.fileSize = file.size;
      throw error;
    }

    try {
      // Check for abort before starting
      if (abortController?.signal?.aborted) {
        throw new Error('Upload cancelled');
      }

      // Check if room is encrypted to determine upload format
      const roomObject = client.getRoom(roomId);
      const isRoomEncrypted = roomObject ? (() => {
        const encryptionEvent = roomObject.currentState.getStateEvents('m.room.encryption', '');
        return !!encryptionEvent;
      })() : false;

      // Upload main file (encrypted or unencrypted)
      let mainFileUrl;
      let mainFileEncryption = null;

      if (isRoomEncrypted) {
        // Encrypted room: Encrypt file before upload
        logger.debug('[useRoom] Encrypting main file for encrypted room:', roomId);

        // Check for abort before expensive encryption operation
        if (abortController?.signal?.aborted) {
          throw new Error('Upload cancelled');
        }

        // Read file as ArrayBuffer for encryption
        const fileArrayBuffer = await file.arrayBuffer();

        // Encrypt the file using matrix-encrypt-attachment
        const encryptedFile = await encryptAttachment(fileArrayBuffer);
        logger.debug('[useRoom] Main file encryption complete, uploading encrypted blob');

        // Upload encrypted file blob
        const encryptedBlob = new Blob([encryptedFile.data]);
        const uploadResponse = await client.uploadContent(encryptedBlob, {
          name: file.name,
          type: 'application/octet-stream', // Encrypted data is binary
          abortController,
          progressHandler: (progress) => {
            if (onProgressUpdate && progress.total > 0) {
              // Main file upload is 80% of total progress (0-80%)
              const percentage = Math.round((progress.loaded / progress.total) * 80);
              onProgressUpdate(Math.max(0, Math.min(80, percentage)));
            }
          },
        });

        mainFileUrl = uploadResponse.content_uri;
        mainFileEncryption = encryptedFile.info;

        logger.debug('[useRoom] Encrypted main file uploaded:', {
          url: mainFileUrl,
          hasKeys: !!(encryptedFile.info.key && encryptedFile.info.iv),
        });

        // Update progress to 85% after encryption and upload complete
        if (onProgressUpdate) {
          onProgressUpdate(85);
        }
      } else {
        // Unencrypted room: Upload file normally
        const uploadResponse = await client.uploadContent(file, {
          name: file.name,
          type: file.type,
          abortController,
          progressHandler: (progress) => {
            if (onProgressUpdate && progress.total > 0) {
              // Main file is 90% of total progress for unencrypted
              const percentage = Math.round((progress.loaded / progress.total) * 90);
              onProgressUpdate(Math.max(0, Math.min(90, percentage)));
            }
          },
        });

        mainFileUrl = uploadResponse.content_uri;
      }

      // Determine message type based on file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');

      let msgtype = 'm.file'; // Default
      if (isImage) msgtype = 'm.image';
      else if (isVideo) msgtype = 'm.video';
      else if (isAudio) msgtype = 'm.audio';

      // Create the basic message content
      const content = {
        body: caption.trim() || file.name,
        filename: file.name,
        info: {
          size: file.size,
          mimetype: file.type,
        },
        msgtype,
        // Use 'file' for encrypted, 'url' for unencrypted (Matrix E2EE spec)
        ...(isRoomEncrypted ? {
          file: {
            url: mainFileUrl,
            key: mainFileEncryption.key,
            iv: mainFileEncryption.iv,
            hashes: mainFileEncryption.hashes,
            v: mainFileEncryption.v,
          },
        } : {
          url: mainFileUrl,
        }),
        ['m.relates_to']: replyEventId ? {
          'm.in_reply_to': {
            'event_id': replyEventId,
          },
        } : undefined,
      };

      // For images and videos, add dimensions and generate thumbnail
      if (isImage || isVideo) {
        // Get media dimensions using utility function
        const mediaDimensions = await getMediaDimensions(file);

        // Add media dimensions to info
        if (mediaDimensions?.w && mediaDimensions?.h) {
          content.info.w = mediaDimensions.w;
          content.info.h = mediaDimensions.h;

          // Add duration for videos
          if (isVideo && mediaDimensions.duration) {
            content.info.duration = mediaDimensions.duration;
          }
        }

        // Generate and upload thumbnail
        try {
          // Check for abort before thumbnail upload
          if (abortController?.signal?.aborted) {
            throw new Error('Upload cancelled');
          }

          // Use pre-generated thumbnail if provided, otherwise generate new one
          let thumbnailToUpload = thumbnailResult;
          if (!thumbnailToUpload) {
            thumbnailToUpload = await generateThumbnail(file);
          }

          if (thumbnailToUpload) {
            // Check for abort before thumbnail upload
            if (abortController?.signal?.aborted) {
              throw new Error('Upload cancelled');
            }

            if (isRoomEncrypted) {
              // Encrypted room: Manually encrypt thumbnail and use thumbnail_file format
              logger.debug('[useRoom] Encrypting thumbnail for encrypted room:', roomId);

              // Read thumbnail as ArrayBuffer for encryption
              const thumbnailArrayBuffer = await thumbnailToUpload.thumbnail.arrayBuffer();

              // Encrypt the thumbnail using matrix-encrypt-attachment
              const encryptedThumbnail = await encryptAttachment(thumbnailArrayBuffer);

              // Upload encrypted thumbnail blob
              const encryptedBlob = new Blob([encryptedThumbnail.data]);
              const thumbnailResponse = await client.uploadContent(encryptedBlob, {
                name: `thumb_${file.name}`,
                type: 'application/octet-stream', // Encrypted data is binary
                abortController,
                progressHandler: (progress) => {
                  if (onProgressUpdate && progress.total > 0) {
                    // Thumbnail is final 15% of progress (85-100%) for encrypted rooms
                    const percentage = 85 + Math.round((progress.loaded / progress.total) * 15);
                    onProgressUpdate(Math.max(85, Math.min(100, percentage)));
                  }
                },
              });

              // Add encrypted thumbnail using thumbnail_file format (Matrix E2EE spec)
              content.info.thumbnail_file = {
                url: thumbnailResponse.content_uri,
                key: encryptedThumbnail.info.key,
                iv: encryptedThumbnail.info.iv,
                hashes: encryptedThumbnail.info.hashes,
                v: encryptedThumbnail.info.v,
              };
              content.info.thumbnail_info = thumbnailToUpload.info.thumbnail_info;

              logger.debug('[useRoom] Encrypted thumbnail uploaded:', {
                url: thumbnailResponse.content_uri,
                hasKeys: !!(encryptedThumbnail.info.key && encryptedThumbnail.info.iv),
              });
            } else {
              // Unencrypted room: Use traditional thumbnail_url format
              const thumbnailResponse = await client.uploadContent(thumbnailToUpload.thumbnail, {
                name: `thumb_${file.name}`,
                type: 'image/jpeg',
                abortController,
                progressHandler: (progress) => {
                  if (onProgressUpdate && progress.total > 0) {
                    // Thumbnail is final 10% of progress (90-100%)
                    const percentage = 90 + Math.round((progress.loaded / progress.total) * 10);
                    onProgressUpdate(Math.max(90, Math.min(100, percentage)));
                  }
                },
              });

              // Add unencrypted thumbnail using thumbnail_url format
              content.info.thumbnail_url = thumbnailResponse.content_uri;
              content.info.thumbnail_info = thumbnailToUpload.info.thumbnail_info;
            }
          }
        } catch (thumbnailError) {
          logger.warn('Failed to generate thumbnail:', thumbnailError);
          // Continue without thumbnail - this is not a critical error
        }
      }

      // Check for abort before sending the message
      if (abortController?.signal?.aborted) {
        throw new Error('Upload cancelled');
      }

      // Send the message with the uploaded file
      // Use a 2-minute timeout to prevent indefinite hangs during E2E key sharing
      logger.debug('[sendAttachment] Sending message event to room', { roomId, msgtype: content.msgtype });

      logger.info('[useRoom] Sending attachment message', {
        roomId,
        effectiveTimeoutMs,
        msgtype: content.msgtype,
      });

      const sendPromise = client.sendEvent(roomId, 'm.room.message', content);
      const result = await raceWithSendTimeout(sendPromise, effectiveTimeoutMs);
      logger.debug('[sendAttachment] Message event sent successfully', { eventId: result?.event_id });

      // Signal 100% completion before returning
      if (onProgressUpdate) {
        onProgressUpdate(100);
      }

      return result;
    } catch (error) {
      // Reset progress on error
      if (onProgressUpdate) {
        onProgressUpdate(-1);
      }

      // Check if this is an AbortError and convert to standard format
      if (error.message?.includes('cancelled') || error.name === 'AbortError') {
        const abortError = new Error('Upload cancelled');
        abortError.name = 'AbortError';
        throw abortError;
      }

      logger.error('Failed to send attachment:', error);
      throw error;
    }
  }, [client, roomId]);

  const enrichedMembers = useMemo(() => {
    if (!members.length || !client || !room) return new Map();

    const memberMap = new Map();

    // Get power levels from room state with validation
    const powerLevelsEvent = room.currentState.getStateEvents('m.room.power_levels', '');
    const powerLevels = powerLevelsEvent?.getContent() || {};

    // Validate users_default is a number, fallback to 0
    const usersDefault = typeof powerLevels.users_default === 'number'
      && !isNaN(powerLevels.users_default)
      ? powerLevels.users_default
      : 0;

    // Validate users object exists and is an object
    const usersMap = powerLevels.users
      && typeof powerLevels.users === 'object'
      && !Array.isArray(powerLevels.users)
      ? powerLevels.users
      : {};

    members.forEach(m => {
      const displayName = m.rawDisplayName || m.name || m.userId;

      // Get avatar from loaded avatar state (async loaded with authentication)
      // Falls back to initials if not yet loaded
      const avatar = memberAvatars.get(m.userId) || generateInitialsAvatar(displayName);

      // Get user's power level with validation
      const rawPowerLevel = usersMap[m.userId];
      const userPowerLevel = typeof rawPowerLevel === 'number'
        && !isNaN(rawPowerLevel)
        ? rawPowerLevel
        : usersDefault;

      memberMap.set(m.userId, {
        ...m, // Keep original member properties
        id: m.userId, // Add id property for ContactList compatibility
        name: displayName,
        avatar: avatar,
        powerLevel: userPowerLevel,
      });
    });

    return memberMap;
  }, [members, client, memberAvatars, room]);

  // Effect: Fetch room avatars - explicit room avatar or fallback to member avatars
  const { getRoomAvatar, getFallbackAvatars } = useAvatar();

  useEffect(() => {
    if (!room || !client) {
      setRoomAvatars([]);
      return;
    }

    let isMounted = true;

    const fetchRoomAvatars = async () => {
      try {
        const roomName = room.name || 'Chat';

        // First, try to get explicit room avatar
        const explicitAvatar = await getRoomAvatar(room.roomId, roomName, 40);

        if (explicitAvatar) {
          // Room has an explicit avatar set
          if (isMounted) {
            setRoomAvatars([explicitAvatar]);
          }
        } else {
          // No explicit avatar - get fallback member avatars
          const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
          if (isMounted) {
            const avatarUrls = fallbackAvatars.map(desc => desc.url).filter(Boolean);
            setRoomAvatars(avatarUrls);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch room avatars:', error);
        if (isMounted) {
          setRoomAvatars([]);
        }
      }
    };

    fetchRoomAvatars();

    return () => {
      isMounted = false;
    };
  }, [room, client, enrichedMembers, getRoomAvatar, getFallbackAvatars]);

  const processMessageEdits = (events) => {
    // Find edit events and track which original events they reference
    const editedEventIds = new Set(); // IDs of original events that have been edited
    const editEventIds = new Set(); // IDs of edit events to filter out
    const editContentMap = new Map(); // Map of original event ID -> new content

    events.forEach(event => {
      // Check both regular content and clear content (for encrypted messages)
      const content = event.getContent();
      const clearContent = event.getClearContent && event.getClearContent();
      const contentToCheck = clearContent || content;

      // Check if this is an edit event
      if (contentToCheck && contentToCheck['m.new_content'] && contentToCheck['m.relates_to']) {
        const relatesTo = contentToCheck['m.relates_to'];

        // Check if it's a replace relation (edit)
        if (relatesTo.rel_type === 'm.replace' && relatesTo.event_id) {
          const originalEventId = relatesTo.event_id;

          // Track that the original event has been edited
          editedEventIds.add(originalEventId);

          // Store the new content for this original event
          editContentMap.set(originalEventId, contentToCheck['m.new_content']);

          // Mark this edit event for removal
          editEventIds.add(event.getId());
        }
      }
    });

    // Filter out edit events and mark original events as edited
    const processedEvents = events.filter(event => {
      return !editEventIds.has(event.getId());
    }).map(event => {
      const eventId = event.getId();

      // Mark event as edited if it has been edited and store the new content
      if (editedEventIds.has(eventId)) {
        event._isEdited = true;
        event._editedContent = editContentMap.get(eventId);
      }

      return event;
    });

    return processedEvents;
  };

  const processReactions = (events) => {
    // Create a map of eventId -> reactions
    const reactionsByMessage = new Map();

    events.forEach(event => {
      if (event.getType() === 'm.reaction' && (event.status == null || event.status === 'sent')) {
        const content = event.getContent();
        const relatesTo = content['m.relates_to'];

        if (relatesTo && relatesTo.rel_type === 'm.annotation' && relatesTo.event_id) {
          const targetEventId = relatesTo.event_id;
          const reactionKey = relatesTo.key;
          const senderId = event.getSender();

          if (!reactionsByMessage.has(targetEventId)) {
            reactionsByMessage.set(targetEventId, new Map());
          }

          const messageReactions = reactionsByMessage.get(targetEventId);
          if (!messageReactions.has(reactionKey)) {
            messageReactions.set(reactionKey, []);
          }

          messageReactions.get(reactionKey).push({
            senderId,
            eventId: event.getId(),
          });
        }
      }
    });

    return reactionsByMessage;
  };

  const messageGroups = useMemo(() => {
    if (!client || !events) return [];

    // Process reactions first to create a map of reactions by message
    const reactionsByMessage = processReactions(events);

    // Filter for supported message types, including encrypted ones and room creation
    const messageEvents = events.filter(event => {
      const type = event.getType();

      // Cancelled local echoes should disappear from the timeline when the user
      // explicitly discards a timed-out message from the retry banner.
      if (event.status === 'cancelled') {
        return false;
      }

      // Filter out messages from ignored users
      const sender = event.getSender();
      if (sender && ignoredUsers.includes(sender)) {
        logger.debug('Filtering out message from ignored user:', sender);
        return false;
      }

      // Filter out redacted reactions (but keep redacted messages)
      if (event.isRedacted()) {
        // If it's a redacted reaction, filter it out
        if (type === 'm.reaction') {
          return false;
        }
        // Keep redacted messages so we can show "Message deleted"
        return true;
      }

      // Include room creation events
      if (type === 'm.room.create') {
        return true;
      }
      // Check if it's a decrypted message (was encrypted but now has clearContent)
      if (event.getClearContent && event.getClearContent()) {
        const clearContent = event.getClearContent();
        return SUPPORTED_MESSAGE_TYPES.includes(clearContent.msgtype);
      }
      // Check regular unencrypted messages
      if (type === 'm.room.message') {
        const content = event.getContent();
        return SUPPORTED_MESSAGE_TYPES.includes(content.msgtype);
      }
      // Include encrypted messages that failed to decrypt
      // so users can at least see that messages exist
      if (type === 'm.room.encrypted') {
        return true;
      }
      // Otherwise include events that typically generate timeline messages
      return TIMELINE_SYSTEM_EVENT_TYPES.includes(type);
    });

    const processedEvents = processMessageEdits(messageEvents);

    const currentUserId = client.getUserId();
    const grouped = groupMessages(processedEvents);

    return grouped.map((messageGroup, groupIndex) => {
      const firstMessage = messageGroup[0];
      const senderId = firstMessage.getSender();
      const messageId = firstMessage.getId();
      const member = enrichedMembers.get(senderId);
      const shield = eventShields.get(messageId);

      // Get display name with fallback to event sender (for users who left)
      // Matrix events preserve sender RoomMember snapshot at time of sending
      const displayName = member?.name || firstMessage.sender?.rawDisplayName || firstMessage.sender?.name || senderId;

      // Add reactions to each message in the group
      const messagesWithReactions = messageGroup.map(message => {
        const messageId = message.getId();
        const messageReactions = reactionsByMessage.get(messageId) || new Map();

        // Enrich reactions with user display names
        const enrichedReactions = new Map();
        messageReactions.forEach((reactors, emoji) => {
          const enrichedReactors = reactors.map(reactor => {
            const reactorMember = enrichedMembers.get(reactor.senderId);
            return {
              ...reactor,
              displayName: reactorMember?.name || reactor.senderId,
              isCurrentUser: reactor.senderId === currentUserId,
            };
          });
          enrichedReactions.set(emoji, enrichedReactors);
        });

        // Attach enriched reactions to message for easy access in components
        message._reactions = enrichedReactions;

        return message;
      });

      // Use member avatar if available (already loaded with authentication)
      // Falls back to initials avatar if not available
      let avatar = member?.avatar;
      if (!avatar) {
        avatar = memberAvatars.get(senderId) || generateInitialsAvatar(displayName);
      }

      return {
        key: `group-${groupIndex}-${messageId}`,
        senderId,
        messages: messagesWithReactions,
        name: displayName,
        avatar: avatar,
        shield: shield,
        isUser: senderId === currentUserId,
      };
    });
  }, [client, events, decryptionCounter, redactionCounter, localEchoCounter, readReceipts, enrichedMembers, eventShields, memberAvatars, ignoredUsers]);

  // Integrate unread tracking
  const { unreadCount, firstUnreadEventId, hasUnread } = useUnreadTracking({
    room,
    client,
    messageGroups,
  });

  return {
    room,
    client,
    roomId,
    name,
    topic,
    members: Array.from(enrichedMembers.values()),
    roomAvatars,
    memberAvatars,
    messageGroups,
    readReceipts,
    toggleReaction,
    sendMessage,
    cancelPendingMessage,
    sendAttachment,
    deleteMessage,
    reportMessage,
    canDeleteMessage,
    powerLevelsCounter,
    leaveRoom,
    isRoomLoading,
    roomExists: !!room,
    isEncrypted,
    isPublic,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
    firstUnreadEventId,
    hasUnread,
  };
};

export default useRoom;
